/**
 * Create-lead identity check — the ONE module that talks to the backend for it.
 *
 * Contract (design doc 2026-10-04-create-lead-identity-check-design.md, 5.2-5.4):
 *   POST /api/v1/practices/{id}/leads/check-identity/        -> check result
 *   POST /api/v1/practices/{id}/leads/                        -> 409 { code: 'lead_identity_match', check }
 *                                                               when matches exist and no `identity_resolution`
 *   POST /api/v1/practices/{id}/leads/{leadId}/restore/?sync=1 -> restored lead (synchronous)
 *
 * If the backend contract changes, adjust the paths / field names here only.
 */
import { callApi } from './apiCall';
import { requestIdentityResolution } from './leadIdentityConflictBus';
import {
    IDENTITY_CACHE_TTL_MS,
    clearIdentityCache,
    deleteIdentityCache,
    readIdentityCache,
    writeIdentityCache,
} from './leadIdentityCache';

export { IDENTITY_CACHE_TTL_MS, clearIdentityCache };

export const IDENTITY_CONFLICT_CODE = 'lead_identity_match';
// The server caps Dentally at 5s; give it a little head-room, then treat the
// whole check as unavailable and let the user continue (server guard is the backstop).
export const IDENTITY_CHECK_TIMEOUT_MS = 7000;
export const IDENTITY_PREFETCH_DEBOUNCE_MS = 600;

export const DENTALLY_STATUS = Object.freeze({
    MATCHED: 'MATCHED',
    ASK: 'ASK',
    AMBIGUOUS: 'AMBIGUOUS',
    NEW: 'NEW',
    UNAVAILABLE: 'UNAVAILABLE',
    NOT_CONNECTED: 'NOT_CONNECTED',
});

const pad = (n) => String(n).padStart(2, '0');

const isRealDate = (y, m, d) => {
    const dt = new Date(Date.UTC(y, m - 1, d));
    return dt.getUTCFullYear() === y && dt.getUTCMonth() === m - 1 && dt.getUTCDate() === d;
};

/**
 * Normalise any date-of-birth shape the create form can hold to YYYY-MM-DD
 * (the format Dentally and the check endpoint expect). Returns '' if unparseable.
 * Accepts Date, moment, 'DD-MM-YYYY', 'DD/MM/YYYY' and 'YYYY-MM-DD[...]'.
 */
export function normaliseDobIso(dob) {
    if (!dob) return '';
    let value = dob;
    if (typeof value === 'object' && typeof value.toDate === 'function') value = value.toDate();
    if (value instanceof Date) {
        if (Number.isNaN(value.getTime())) return '';
        return `${value.getFullYear()}-${pad(value.getMonth() + 1)}-${pad(value.getDate())}`;
    }
    const s = String(value).trim();
    let m = /^(\d{4})-(\d{2})-(\d{2})(?:$|[T\s])/.exec(s);
    if (m) {
        const [y, mo, d] = [Number(m[1]), Number(m[2]), Number(m[3])];
        return isRealDate(y, mo, d) ? `${m[1]}-${m[2]}-${m[3]}` : '';
    }
    m = /^(\d{1,2})[-/](\d{1,2})[-/](\d{4})$/.exec(s);
    if (m) {
        const [d, mo, y] = [Number(m[1]), Number(m[2]), Number(m[3])];
        return isRealDate(y, mo, d) ? `${y}-${pad(mo)}-${pad(d)}` : '';
    }
    return '';
}

const digitsOnly = (v) => String(v || '').replace(/\D/g, '');
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

export const isCheckablePhone = (phone) => digitsOnly(phone).length >= 7;
export const isCheckableEmail = (email) => EMAIL_RE.test(String(email || '').trim());
export const hasCheckableContact = (fields) => isCheckablePhone(fields?.phone) || isCheckableEmail(fields?.email);

/** Build the check request body from form-ish values (RHF values or personalDetail). */
export function buildIdentityFields(src = {}) {
    const phone = src.phone ?? src.phoneNumber ?? src.lead_contact?.phone ?? '';
    const email = src.email ?? src.lead_contact?.email ?? '';
    return {
        first_name: String(src.first_name ?? src.firstName ?? '').trim(),
        last_name: String(src.last_name ?? src.lastName ?? '').trim(),
        email: String(email || '').trim(),
        phone: String(phone || '').trim(),
        dob: normaliseDobIso(src.dob),
    };
}

export const identityCacheKey = (practiceId, fields) => [
    practiceId,
    digitsOnly(fields.phone),
    String(fields.email || '').toLowerCase(),
    fields.dob || '',
    String(fields.first_name || '').toLowerCase(),
    String(fields.last_name || '').toLowerCase(),
].join('|');

/** Make sure the check always has the shape the UI reads. */
export function normaliseCheck(raw) {
    const crm = raw?.crm || {};
    const active = Array.isArray(crm.active) ? crm.active : [];
    const archived = Array.isArray(crm.archived) ? crm.archived : [];
    const dentally = raw?.dentally || {};
    const status = dentally.status || DENTALLY_STATUS.NOT_CONNECTED;
    return {
        crm: { active, archived },
        dentally: {
            status,
            candidates: Array.isArray(dentally.candidates) ? dentally.candidates : [],
        },
        has_crm_match: typeof raw?.has_crm_match === 'boolean' ? raw.has_crm_match : active.length + archived.length > 0,
    };
}

/** True when the check has anything the operator has to decide on. */
export function checkNeedsDecision(check) {
    if (!check) return false;
    const { crm, dentally } = check;
    if (crm.active.length || crm.archived.length) return true;
    return dentally.status === DENTALLY_STATUS.MATCHED
        || dentally.status === DENTALLY_STATUS.ASK
        || dentally.status === DENTALLY_STATUS.AMBIGUOUS;
}

/**
 * Run the check. Never throws. Resolves to
 *   { ok: true, check }  or  { ok: false, timedOut, error }
 * `callApi` is given the same timeout so a stalled socket is also released.
 */
export async function checkLeadIdentity(practiceId, fields, { timeoutMs = IDENTITY_CHECK_TIMEOUT_MS } = {}) {
    let timer;
    const timeout = new Promise((resolve) => {
        timer = setTimeout(() => resolve({ ok: false, timedOut: true, error: 'timeout' }), timeoutMs);
    });
    const request = (async () => {
        try {
            const res = await callApi(
                `/api/v1/practices/${practiceId}/leads/check-identity/`,
                'post',
                fields,
                false,
                { timeout: timeoutMs, suppressServerErrorDispatch: true },
            );
            if (res?.ok === false || (res?.status && res.status >= 400) || !res?.data) {
                return { ok: false, timedOut: false, error: res?.status || 'error' };
            }
            return { ok: true, check: normaliseCheck(res.data) };
        } catch (e) {
            return { ok: false, timedOut: false, error: e?.message || 'error' };
        }
    })();
    try {
        return await Promise.race([request, timeout]);
    } finally {
        clearTimeout(timer);
    }
}

/**
 * Cached wrapper: identical inputs within the TTL share one request (prefetch on
 * blur, then the authoritative check on Next reuses it). Failed results are not cached.
 */
export function getIdentityCheck(practiceId, fields, opts) {
    const key = identityCacheKey(practiceId, fields);
    const hit = readIdentityCache(key); // evicts an expired entry
    if (hit) return hit.promise;
    const promise = checkLeadIdentity(practiceId, fields, opts).then((result) => {
        if (!result.ok) deleteIdentityCache(key);
        return result;
    });
    writeIdentityCache(key, promise);
    return promise;
}

/** 409 from the create endpoint carrying the identity check. */
export const isIdentityConflict = (response) => (
    response?.status === 409 && response?.data?.code === IDENTITY_CONFLICT_CODE
);
export const extractIdentityConflict = (response) => normaliseCheck(response?.data?.check);

/**
 * Build the `identity_resolution` request field.
 *   dentallyCheck: 'matched' | 'declined' | 'none' | 'unavailable'
 */
export function buildIdentityResolution({
    decision = 'create_new', acknowledgedLeadIds = [], dentallyPatientId = null, dentallyCheck = 'none',
} = {}) {
    return {
        decision,
        acknowledged_lead_ids: acknowledgedLeadIds,
        dentally_patient_id: dentallyPatientId != null && dentallyPatientId !== '' ? String(dentallyPatientId) : null,
        dentally_check: dentallyCheck,
    };
}

/** The ids of every CRM lead the operator has seen in a check. */
export const collectLeadIds = (check) => [
    ...(check?.crm?.active || []),
    ...(check?.crm?.archived || []),
].map((l) => l.id).filter((id) => id != null);

/** Map the Dentally part of a check + the user's pick to the resolution's dentally_check. */
export function dentallyResolutionFor(check, selectedPatientId) {
    const status = check?.dentally?.status;
    if (status === DENTALLY_STATUS.UNAVAILABLE) return { dentallyPatientId: null, dentallyCheck: 'unavailable' };
    if (status === DENTALLY_STATUS.MATCHED || status === DENTALLY_STATUS.ASK || status === DENTALLY_STATUS.AMBIGUOUS) {
        if (selectedPatientId) return { dentallyPatientId: selectedPatientId, dentallyCheck: 'matched' };
        return { dentallyPatientId: null, dentallyCheck: 'declined' };
    }
    return { dentallyPatientId: null, dentallyCheck: 'none' };
}

/**
 * Restore an archived lead SYNCHRONOUSLY so the wizard can continue into the
 * follow-up flow. Resolves { ok: true, lead } or { ok: false, message }.
 * An old backend that ignores `sync` answers `{ scheduled: true }`, which cannot
 * be continued from, so it is reported as a failure.
 */
export async function restoreLeadSync(practiceId, leadId) {
    try {
        const res = await callApi(`/api/v1/practices/${practiceId}/leads/${leadId}/restore/?sync=1`, 'post', {});
        if (res?.ok && res?.data && !(res.data.scheduled === true && !res.data.id && !res.data.lead)) {
            const lead = res.data.lead || res.data;
            return { ok: true, lead };
        }
        if (res?.ok) return { ok: false, message: 'The lead is being restored in the background. Try again in a few minutes.' };
        const detail = res?.data?.detail;
        return { ok: false, message: typeof detail === 'string' ? detail : 'Could not restore this lead. Please try again.' };
    } catch (e) {
        return { ok: false, message: 'Could not restore this lead. Please try again.' };
    }
}

// ── Create-lead identity guard ───────────────────────────────
// The backend answers 409 `lead_identity_match` when the manual create finds an
// existing active/archived lead and the request carries no `identity_resolution`.
// Every manual create path goes through this helper: on 409 it asks the operator
// (compact dialog) and retries ONCE with the chosen resolution. A cancelled
// dialog resolves { cancelled: true } and nothing is created.
export async function postManualLead(practiceId, payload, callOptions, initialResolution = null, onSubmittedPayload = null) {
    let resolution = initialResolution;
    let response;
    // Two attempts: the original, then one retry with the operator's resolution.
    for (let attempt = 0; attempt < 2; attempt += 1) {
        const body = resolution ? { ...payload, identity_resolution: resolution } : payload;
        const url = `/api/v1/practices/${practiceId}/leads/`;
        onSubmittedPayload?.(body);
        // eslint-disable-next-line no-await-in-loop
        response = await (callOptions ? callApi(url, 'post', body, false, callOptions) : callApi(url, 'post', body));
        if (!isIdentityConflict(response)) return { response };
        // eslint-disable-next-line no-await-in-loop
        const choice = await requestIdentityResolution(extractIdentityConflict(response));
        if (!choice || choice.cancelled || !choice.resolution) return { response, cancelled: true, choice };
        ({ resolution } = choice);
    }
    return { response };
}

/**
 * Full (slim) lead for the follow-up flow. The identity check only returns masked
 * summaries, but the follow-up screens need the real contact + treatments.
 * Falls back to a minimal lead built from the summary so the flow can still open.
 */
export async function fetchLeadForFollowUp(practiceId, summary) {
    try {
        const res = await callApi(`/api/v1/practices/${practiceId}/leads/${summary.id}/?slim=true`);
        if (res?.status === 200 && res.data?.id) return res.data;
    } catch (e) { /* fall through to the summary */ }
    const [first = '', ...rest] = String(summary.name || '').split(' ');
    return { id: summary.id, first_name: first, last_name: rest.join(' '), lead_contact: {}, lead_treatments: [] };
}
